import { WorkflowRepairOutcome } from '@atlas/types'
import { ListSkeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge } from '@atlas/ui-primitives'
import type { WorkflowRepairReport } from '@/domain/workflow-admin.types'
import { REPAIR_OUTCOME_APPLIED_LABEL, REPAIR_OUTCOME_PREVIEW_LABEL } from '@/domain/workflow-admin.types'
import { EmptyState, ErrorState } from './states'

/**
 * A repair report, preview or applied. The counts are always derived from the
 * per-order outcomes: the report's aggregate `repaired` is zero on a dry run by
 * definition, so using it as the previewed repair count would tell an
 * administrator nothing would be fixed when in fact it would.
 */

export interface RepairResultsProps {
	report?: WorkflowRepairReport
	isLoading?: boolean
	error?: unknown
	onRetry?: () => void
}

const OUTCOMES = Object.values(WorkflowRepairOutcome)

const countByOutcome = (report: WorkflowRepairReport): Record<WorkflowRepairOutcome, number> => {
	const counts = Object.fromEntries(OUTCOMES.map((outcome) => [outcome, 0])) as Record<WorkflowRepairOutcome, number>
	for (const result of report.results) counts[result.outcome] += 1
	return counts
}

const badgeVariantOf = (outcome: WorkflowRepairOutcome) => {
	if (outcome === WorkflowRepairOutcome.REPAIRED) return 'primary' as const
	if (outcome === WorkflowRepairOutcome.NO_OP || outcome === WorkflowRepairOutcome.TERMINAL_SKIPPED) {
		return 'secondary' as const
	}
	return 'danger' as const
}

export function RepairResults({ report, isLoading, error, onRetry }: RepairResultsProps) {
	if (isLoading) return <ListSkeleton rows={3} label="Running repair" />
	if (error) return <ErrorState error={error} onRetry={onRetry} title="Repair failed" />
	if (!report) return null

	if (report.results.length === 0) {
		return <EmptyState title="No repair candidates" description="No Work Orders were selected for repair." />
	}

	const labels = report.dryRun ? REPAIR_OUTCOME_PREVIEW_LABEL : REPAIR_OUTCOME_APPLIED_LABEL
	const counts = countByOutcome(report)
	// A batch is only "successful" when nothing was refused; anything else is
	// reported as a partial result so a refusal is never read as a success.
	const refused = counts.STATUS_NOT_AVAILABLE + counts.UNRESOLVABLE
	const heading = report.dryRun
		? `Preview — nothing has been written`
		: refused > 0
			? `Applied with ${refused} refused`
			: `Applied`

	return (
		<div className="space-y-3" data-repair-mode={report.dryRun ? 'preview' : 'applied'}>
			<p className="font-medium">{heading}</p>
			<ul className="flex flex-wrap gap-4 text-sm">
				{OUTCOMES.map((outcome) => (
					<li key={outcome}>
						<span className="text-ds-text-muted">{labels[outcome]}: </span>
						<span data-testid={`repair-count-${outcome}`}>{counts[outcome]}</span>
					</li>
				))}
			</ul>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Work Order</TableHead>
						<TableHead>Outcome</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Workflow</TableHead>
						<TableHead>Detail</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{report.results.map((result) => (
						<TableRow key={result.workOrderId}>
							<TableCell className="text-ds-text-muted">{result.workOrderId}</TableCell>
							<TableCell>
								<Badge variant={badgeVariantOf(result.outcome)} size="sm">
									{labels[result.outcome]}
								</Badge>
							</TableCell>
							<TableCell>{result.statusCode}</TableCell>
							<TableCell className="text-ds-text-muted text-sm">
								{result.previousWorkflowId ?? '—'} → {result.resolvedWorkflowId ?? '—'}
							</TableCell>
							<TableCell className="text-sm text-ds-text-muted">{result.detail}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	)
}
